import React, { useContext, useState } from 'react';
import { iterationKey, repeatIfDaily } from '../components/calendar/utils';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PraksisgrupperPerInstitusjon from './praksisgrupperPerInstitusjon';
import alpColors from '../alpColors';
import { CalendarPsaContext } from './calendarPsaContext';
import {
  PraksisgruppeDto,
  PraksiskalenderAktivitetDto,
} from '../api/generated/praksissted-generated-api';
import { WeekYear } from '../date.utils';
import { styled } from '@mui/system';

const StyledAccountBalanceIcon = styled(AccountBalanceIcon)({
  fontSize: 20,
  verticalAlign: 'bottom',
  marginRight: '16px',
  color: alpColors.pewter,
});

interface InstitutionHeaderProps {
  institutionName: string;
  columnsCount: number;
  collapsible: boolean;
  collapsed: boolean;
  onCollapseToggle: (b: boolean) => void;
}

const InstitutionHeader: React.FC<
  React.PropsWithChildren<InstitutionHeaderProps>
> = ({
  institutionName,
  columnsCount,
  collapsible,
  collapsed,
  onCollapseToggle,
}) => {
  const collapseFunction = collapsible
    ? () => {
        onCollapseToggle(!collapsed);
      }
    : () => {};

  return (
    <thead>
      <tr onClick={collapseFunction}>
        <th colSpan={columnsCount + 1} className="institution-slug">
          <StyledAccountBalanceIcon />
          {institutionName}
          {collapsible ? (
            <div className="collapse-toggle">
              {collapsed ? <ArrowDownIcon /> : <ArrowUpIcon />}
            </div>
          ) : (
            ''
          )}
        </th>
      </tr>
    </thead>
  );
};

interface CalendarPerInstitutionProps {
  praksisstednavn: string;
  orderNo: number;
  praksisgrupperFiltered: PraksisgruppeDto[];
  practiceActivitiesFiltered: {
    [klasseId: string]: PraksiskalenderAktivitetDto[];
  };
}

const CalendarPerPraksissted: React.FC<
  React.PropsWithChildren<CalendarPerInstitutionProps>
> = ({
  orderNo,
  praksisstednavn,
  praksisgrupperFiltered,
  practiceActivitiesFiltered,
}) => {
  const { weekNumbers, isWeekly } = useContext(CalendarPsaContext);
  const [collapsed, setCollapsed] = useState(orderNo > 0);
  const collapsible = false;

  if (praksisgrupperFiltered.length === 0) {
    return <></>;
  }

  return (
    <div
      className={`calendar-width-limiter ${
        collapsible && collapsed ? 'collapsed' : ''
      }`}
    >
      <table className="calendar-per-praksissted">
        <InstitutionHeader
          institutionName={praksisstednavn}
          columnsCount={weekNumbers.length * (isWeekly ? 1 : 7)}
          collapsible={collapsible}
          collapsed={collapsed}
          onCollapseToggle={(shouldCollapse: boolean) =>
            setCollapsed(shouldCollapse)
          }
        />
        <tbody>
          {praksisgrupperFiltered.map((it) => (
            <PraksisgrupperPerInstitusjon
              key={it.praksis.kode}
              praksisgruppe={it}
              filteredAktiviteterByKlasseId={practiceActivitiesFiltered}
            />
          ))}
          <tr className="divider">
            <td>&nbsp;</td>
            {weekNumbers.map((it: WeekYear) =>
              repeatIfDaily(isWeekly, (iDay) => (
                <td key={iterationKey(it, iDay)}>&nbsp;</td>
              )),
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default CalendarPerPraksissted;
